import { useState, useMemo } from 'react';
import {
  Container,
  Box,
  Flex,
  Button,
  Heading,
  Text,
  Select,
  HStack,
  VStack,
  useDisclosure,
  useToast,
  useColorModeValue,
  Icon,
  Collapse,
} from '@chakra-ui/react';
import { Card } from '../../../components/Card';
import { AddIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Square, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useIssues } from './useIssues';
import { useDepartments } from '../Departments/useDepartments';
import { AddIssueModal } from './components/AddIssueModal';
import { IssueDetailsModal } from './components/IssueDetailsModal';
import { ViewToggle, ViewMode } from './components/ViewToggle';
import { IssuesTable } from './components/IssuesTable';
import { IssuesCardView } from './components/IssuesCardView';
import { ResolveIssueModal } from '../Rocks/components/ResolveIssueModal';
import { Issue } from '../types';

/**
 * Issues Page - Main page for viewing and managing Issues
 * Redesigned with Compact and Comfortable view modes
 */
export function IssuesPage() {
  const { currentUser } = useAuth();
  const { issues, actionItems, users, rocks, loading, createIssue, updateIssue, resolveIssue } =
    useIssues(currentUser?.uid);
  const { departments } = useDepartments();

  const [viewMode, setViewMode] = useState<ViewMode>('compact');
  const [openIssuesExpanded, setOpenIssuesExpanded] = useState(true);
  const [resolvedIssuesExpanded, setResolvedIssuesExpanded] = useState(true);

  const [filters, setFilters] = useState({
    view: 'all' as 'all' | 'open' | 'resolved',
    status: 'all' as 'all' | 'open' | 'in-progress' | 'solved',
    assigned: 'everyone' as 'everyone' | string,
    quickFilter: 'all' as 'all' | 'rock' | 'company' | 'my' | 'off-track',
  });

  const {
    isOpen: isFormOpen,
    onOpen: onFormOpen,
    onClose: onFormClose,
  } = useDisclosure();

  const {
    isOpen: isResolveModalOpen,
    onOpen: onResolveModalOpen,
    onClose: onResolveModalClose,
  } = useDisclosure();

  const {
    isOpen: isDetailsModalOpen,
    onOpen: onDetailsModalOpen,
    onClose: onDetailsModalClose,
  } = useDisclosure();

  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();

  const [issueToResolve, setIssueToResolve] = useState<Issue | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);

  const toast = useToast();

  const handleSaveIssue = async (formData: any) => {
    try {
      await createIssue(formData);
      toast({
        title: 'Issue created',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onFormClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleResolveIssue = async (issueId: string) => {
    const issue = issues.find((i) => i.id === issueId);
    if (!issue) return;

    // Show the modal for all issues to collect resolution notes
    setIssueToResolve(issue);
    onResolveModalOpen();
  };

  const handleConfirmResolve = async (resolutionNotes: string) => {
    if (!issueToResolve) return;

    try {
      await resolveIssue(issueToResolve.id, resolutionNotes, currentUser?.uid);
      toast({
        title: 'Issue resolved',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onResolveModalClose();
      setIssueToResolve(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to resolve issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleViewIssue = (issueId: string) => {
    const issue = issues.find((i) => i.id === issueId);
    if (issue) {
      setSelectedIssue(issue);
      onDetailsModalOpen();
    }
  };

  const handleEditIssue = (issue: Issue) => {
    setSelectedIssue(issue);
    onDetailsModalClose();
    onEditModalOpen();
  };

  const handleSaveEdit = async (formData: any) => {
    if (!selectedIssue) return;

    try {
      // Convert empty strings to null for optional fields
      const updateData: any = {
        name: formData.name,
        description: formData.description,
        status: formData.status,
        isCompany: formData.isCompany,
      };

      // Handle optional fields
      if (formData.assignedToUserId !== undefined) {
        updateData.assignedToUserId = formData.assignedToUserId || null;
      }
      if (formData.departmentId !== undefined) {
        updateData.departmentId = formData.departmentId || null;
      }
      if (formData.relatedRockId !== undefined) {
        updateData.relatedRockId = formData.relatedRockId || null;
      }
      if (formData.relatedMetricId !== undefined) {
        updateData.relatedMetricId = formData.relatedMetricId || null;
      }

      await updateIssue(selectedIssue.id, updateData);
      toast({
        title: 'Issue updated',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      onEditModalClose();
      setSelectedIssue(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleResolveFromDetails = async (issueId: string, resolutionNotes: string) => {
    try {
      await resolveIssue(issueId, resolutionNotes, currentUser?.uid);
      toast({
        title: 'Issue resolved',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to resolve issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Filter issues based on current filters
  const filteredIssues = useMemo(() => {
    return issues.filter((issue) => {
      // Status filter
      if (filters.status !== 'all' && issue.status !== filters.status) return false;

      // Assigned filter
      if (filters.assigned !== 'everyone') {
        if (issue.assignedToUserId !== filters.assigned) return false;
      }

      // Quick filters
      if (filters.quickFilter === 'rock' && !issue.relatedRockId) return false;
      if (filters.quickFilter === 'company' && !issue.isCompany) return false;
      if (filters.quickFilter === 'my' && issue.assignedToUserId !== currentUser?.uid) return false;
      if (filters.quickFilter === 'off-track') {
        // Off-track rocks have issues
        if (!issue.relatedRockId) return false;
        const relatedRock = rocks.find((r) => r.id === issue.relatedRockId);
        if (!relatedRock || relatedRock.status !== 'off-track') return false;
      }

      return true;
    });
  }, [issues, filters, currentUser?.uid, rocks]);

  // Separate open and resolved issues
  // Note: Resolved issues are already filtered to last 30 days at the Firestore query level
  const openIssues = useMemo(
    () => filteredIssues.filter((i) => i.status === 'open' || i.status === 'in-progress'),
    [filteredIssues]
  );
  const resolvedIssues = useMemo(
    () => filteredIssues.filter((i) => i.status === 'solved'),
    [filteredIssues]
  );

  // Calculate counts for quick filters
  const allCount = filteredIssues.length;
  const rockIssuesCount = issues.filter((i) => i.relatedRockId).length;
  const companyIssuesCount = issues.filter((i) => i.isCompany).length;
  const myIssuesCount = issues.filter((i) => i.assignedToUserId === currentUser?.uid).length;
  const offTrackRocksCount = issues.filter((i) => {
    if (!i.relatedRockId) return false;
    const rock = rocks.find((r) => r.id === i.relatedRockId);
    return rock?.status === 'off-track';
  }).length;

  const cardBg = useColorModeValue('white', 'gray.700');
  const openBorderColor = useColorModeValue('blue.300', 'blue.600');
  const resolvedBorderColor = useColorModeValue('green.300', 'green.600');
  const quickFilterActiveBg = useColorModeValue('blue.500', 'blue.600');
  const quickFilterInactiveBg = useColorModeValue('gray.100', 'gray.700');
  const quickFilterActiveColor = 'white';
  const quickFilterInactiveColor = useColorModeValue('gray.600', 'gray.400');
  const quickFilterHoverBg = useColorModeValue('gray.200', 'gray.600');

  if (loading) {
    return (
      <Container maxW="container.xl" py={8} overflowX="hidden" w="100%">
        <Text>Loading issues...</Text>
      </Container>
    );
  }

  return (
    <Container  maxW="container.xl" py={8} overflowX="hidden" w="100%">
      {/* Header */}
      <Box mb={6} w="100%" maxWidth='90vw'>
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={4}>
          <Box>
            <Heading size="lg">Issues</Heading>
            <Text color="gray.600" mt={1}>
              Track and resolve issues across the organization
            </Text>
          </Box>
          <HStack spacing={3}>
            <HStack spacing={2}>
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                View:
              </Text>
              <ViewToggle value={viewMode} onChange={setViewMode} />
            </HStack>
            <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={onFormOpen}>
              Add Company Issue
            </Button>
          </HStack>
        </Flex>

        {/* Filter Section */}
        <Card mb={6} borderLeft="4px" borderLeftColor={openBorderColor} w="100%" overflowX="hidden">
          <Box p={4} w="100%">
            <VStack align="stretch" spacing={4}>
              {/* Dropdown Filters */}
              <HStack spacing={4} flexWrap="wrap" align="flex-end">
                <Box textAlign="center">
                  <Text fontSize="sm" mb={0} fontWeight="medium" color="gray.700">
                    View:
                  </Text>
                  <Select
                    value={filters.view}
                    onChange={(e) =>
                      setFilters({ ...filters, view: e.target.value as any })
                    }
                    size="sm"
                    minW="140px"
                    bg={cardBg}
                    mt={0}
                  >
                    <option value="all">All Issues</option>
                    <option value="open">Open Issues</option>
                    <option value="resolved">Resolved Issues</option>
                  </Select>
                </Box>

                <Box textAlign="center">
                  <Text fontSize="sm" mb={0} fontWeight="medium" color="gray.700">
                    Status:
                  </Text>
                  <Select
                    value={filters.status}
                    onChange={(e) =>
                      setFilters({ ...filters, status: e.target.value as any })
                    }
                    size="sm"
                    minW="140px"
                    bg={cardBg}
                    mt={0}
                  >
                    <option value="all">All</option>
                    <option value="open">Open</option>
                    <option value="in-progress">In Progress</option>
                    <option value="solved">Resolved</option>
                  </Select>
                </Box>

                <Box textAlign="center">
                  <Text fontSize="sm" mb={0} fontWeight="medium" color="gray.700">
                    Assigned:
                  </Text>
                  <Select
                    value={filters.assigned}
                    onChange={(e) =>
                      setFilters({ ...filters, assigned: e.target.value })
                    }
                    size="sm"
                    minW="160px"
                    bg={cardBg}
                    mt={0}
                  >
                    <option value="everyone">Everyone</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </Select>
                </Box>
              </HStack>

              {/* Quick Filters */}
              <Box>
                <Text fontSize="sm" mb={2} fontWeight="medium" color="gray.700">
                  Quick Filters:
                </Text>
                <HStack spacing={2} flexWrap="wrap">
                  <Button
                    size="sm"
                    bg={
                      filters.quickFilter === 'all'
                        ? quickFilterActiveBg
                        : quickFilterInactiveBg
                    }
                    color={
                      filters.quickFilter === 'all'
                        ? quickFilterActiveColor
                        : quickFilterInactiveColor
                    }
                    onClick={() => setFilters({ ...filters, quickFilter: 'all' })}
                    _hover={{
                      bg:
                        filters.quickFilter === 'all'
                          ? quickFilterActiveBg
                          : quickFilterHoverBg,
                    }}
                    borderRadius="full"
                    px={4}
                  >
                    All ({allCount})
                  </Button>
                  <Button
                    size="sm"
                    bg={
                      filters.quickFilter === 'rock'
                        ? quickFilterActiveBg
                        : quickFilterInactiveBg
                    }
                    color={
                      filters.quickFilter === 'rock'
                        ? quickFilterActiveColor
                        : quickFilterInactiveColor
                    }
                    onClick={() => setFilters({ ...filters, quickFilter: 'rock' })}
                    _hover={{
                      bg:
                        filters.quickFilter === 'rock'
                          ? quickFilterActiveBg
                          : quickFilterHoverBg,
                    }}
                    borderRadius="full"
                    px={4}
                  >
                    Rock Issues ({rockIssuesCount})
                  </Button>
                  <Button
                    size="sm"
                    bg={
                      filters.quickFilter === 'company'
                        ? quickFilterActiveBg
                        : quickFilterInactiveBg
                    }
                    color={
                      filters.quickFilter === 'company'
                        ? quickFilterActiveColor
                        : quickFilterInactiveColor
                    }
                    onClick={() => setFilters({ ...filters, quickFilter: 'company' })}
                    _hover={{
                      bg:
                        filters.quickFilter === 'company'
                          ? quickFilterActiveBg
                          : quickFilterHoverBg,
                    }}
                    borderRadius="full"
                    px={4}
                  >
                    Company Issues ({companyIssuesCount})
                  </Button>
                  <Button
                    size="sm"
                    bg={
                      filters.quickFilter === 'my'
                        ? quickFilterActiveBg
                        : quickFilterInactiveBg
                    }
                    color={
                      filters.quickFilter === 'my'
                        ? quickFilterActiveColor
                        : quickFilterInactiveColor
                    }
                    onClick={() => setFilters({ ...filters, quickFilter: 'my' })}
                    _hover={{
                      bg:
                        filters.quickFilter === 'my'
                          ? quickFilterActiveBg
                          : quickFilterHoverBg,
                    }}
                    borderRadius="full"
                    px={4}
                  >
                    My Issues ({myIssuesCount})
                  </Button>
                  <Button
                    size="sm"
                    bg={
                      filters.quickFilter === 'off-track'
                        ? quickFilterActiveBg
                        : quickFilterInactiveBg
                    }
                    color={
                      filters.quickFilter === 'off-track'
                        ? quickFilterActiveColor
                        : quickFilterInactiveColor
                    }
                    onClick={() => setFilters({ ...filters, quickFilter: 'off-track' })}
                    _hover={{
                      bg:
                        filters.quickFilter === 'off-track'
                          ? quickFilterActiveBg
                          : quickFilterHoverBg,
                    }}
                    borderRadius="full"
                    px={4}
                  >
                    Off-Track Rocks ({offTrackRocksCount})
                  </Button>
                </HStack>
              </Box>
            </VStack>
          </Box>
        </Card>
      </Box>

      {/* Open Issues Section */}
      <Card mb={6} borderLeft="4px" borderLeftColor={openBorderColor} w="100%" maxW="100%" overflowX="hidden">
        <Box
          p={4}
          cursor="pointer"
          onClick={() => setOpenIssuesExpanded(!openIssuesExpanded)}
        >
          <Flex justify="space-between" align="center">
            <HStack spacing={2}>
              <Icon as={Square} w={5} h={5} color="gray.400" />
              <Heading size="md">OPEN ISSUES ({openIssues.length})</Heading>
            </HStack>
            <Icon
              as={openIssuesExpanded ? ChevronUpIcon : ChevronDownIcon}
              w={5}
              h={5}
              color="gray.600"
            />
          </Flex>
        </Box>
        <Collapse in={openIssuesExpanded} animateOpacity>
          <Box px={4} pb={4} overflowX="auto" maxW="100%" w="100%">
            {openIssues.length === 0 ? (
              <Text color="gray.500" textAlign="center" py={8}>
                No open issues
              </Text>
            ) : viewMode === 'compact' ? (
              <IssuesTable
                issues={openIssues}
                actionItems={actionItems}
                users={users}
                rocks={rocks}
                onResolve={handleResolveIssue}
                onView={handleViewIssue}
              />
            ) : (
              <IssuesCardView
                issues={openIssues}
                actionItems={actionItems}
                users={users}
                rocks={rocks}
                onResolve={handleResolveIssue}
                onView={handleViewIssue}
              />
            )}
          </Box>
        </Collapse>
      </Card>

      {/* Resolved Issues Section */}
      <Card mb={6} borderLeft="4px" borderLeftColor={resolvedBorderColor} w="100%" maxW="100%" overflowX="hidden">
        <Box
          p={4}
          cursor="pointer"
          onClick={() => setResolvedIssuesExpanded(!resolvedIssuesExpanded)}
        >
          <Flex justify="space-between" align="center">
            <HStack spacing={2}>
              <Icon as={CheckCircle2} w={5} h={5} color="green.500" />
              <Heading size="md">RESOLVED ISSUES ({resolvedIssues.length})</Heading>
            </HStack>
            <Icon
              as={resolvedIssuesExpanded ? ChevronUpIcon : ChevronDownIcon}
              w={5}
              h={5}
              color="gray.600"
            />
          </Flex>
        </Box>
        <Collapse in={resolvedIssuesExpanded} animateOpacity>
          <Box px={4} pb={4} overflowX="auto" maxW="100%" w="100%">
            {resolvedIssues.length === 0 ? (
              <Text color="gray.500" textAlign="center" py={8}>
                No resolved issues
              </Text>
            ) : viewMode === 'compact' ? (
              <IssuesTable
                issues={resolvedIssues}
                actionItems={actionItems}
                users={users}
                rocks={rocks}
                onView={handleViewIssue}
              />
            ) : (
              <IssuesCardView
                issues={resolvedIssues}
                actionItems={actionItems}
                users={users}
                rocks={rocks}
                onView={handleViewIssue}
              />
            )}
          </Box>
        </Collapse>
      </Card>

      <AddIssueModal
        isOpen={isFormOpen}
        onClose={onFormClose}
        onSave={handleSaveIssue}
        currentUserId={currentUser?.uid}
        isCompany={true}
        departments={departments}
        users={users}
        modalTitle="Add Company Issue"
        submitButtonText="Add Company Issue"
      />

      <AddIssueModal
        isOpen={isEditModalOpen}
        onClose={() => {
          onEditModalClose();
          setSelectedIssue(null);
        }}
        onSave={handleSaveEdit}
        currentUserId={currentUser?.uid}
        initialIssue={selectedIssue || undefined}
        mode="edit"
        departments={departments}
        rocks={rocks}
      />

      <IssueDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          onDetailsModalClose();
          setSelectedIssue(null);
        }}
        issue={selectedIssue}
        users={users}
        rocks={rocks}
        onEdit={handleEditIssue}
        onResolve={handleResolveFromDetails}
        currentUserId={currentUser?.uid}
      />

      {issueToResolve && (
        <ResolveIssueModal
          isOpen={isResolveModalOpen}
          onClose={() => {
            onResolveModalClose();
            setIssueToResolve(null);
          }}
          onConfirm={handleConfirmResolve}
          issueTitle={issueToResolve.name}
        />
      )}
    </Container>
  );
}
